import { useState, useMemo } from 'react';
import noop from 'utils/noop';
import { useTranslation } from 'modules/queryBuilder/hooks/useTranslation';
import { InputTextProps } from 'ui/inputs';
import { SelectValueType } from 'ui/selects';
import { isNull, isString, isDefined, isUndefined } from 'utils/tsHelpers';
import { isEmptyString } from 'utils/stringHelpers';

import { getOnInputChange } from './utils';
import {
  EllipsizedContent,
  ExpandedSelectOptionsContainer,
  StyledInputSelect,
  StyledSelectOptionsContainer,
  StyledSelectWrapper,
} from './ConditionInput.styles';
import { ConditionTriggerButton } from '../TriggerButton';
import { ConditionSingleValueSelectProps } from '../types';
import { getSelectVariables } from '../utils';
import { ConditionInputWithoutOptions } from './ConditionInputWithoutOptions';
import { TruncatedTooltipBasicOption } from '../TruncatedTooltipBasicOption';

export const DATA_TEST = {
  inputWithoutOptions: 'condition-single-value-input-withoutOptions',
  inputWithOptions: 'condition-single-value-input-withOptions',
};

export const ConditionSingleValueInput = <TValue extends SelectValueType = string>({
  isDisabled = false,
  isReadOnly = false,
  onChange = noop,
  options,
  placeholder = '',
  type,
  value,
  setSearch,
  triggerTooltipContent,
  width,
  isOpenByDefault = false,
  isVirtualized = false,
  ...props
}: ConditionSingleValueSelectProps<TValue> &
  Pick<InputTextProps, 'type' | 'minValue' | 'maxValue' | 'width' | 'autoFocus'>) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState<boolean>(isOpenByDefault);
  const { baseOptions, defaultValues, selectedValue } = useMemo(
    () => getSelectVariables(value, options ?? [], isVirtualized),
    [value, options, isVirtualized],
  );
  const isEmpty = isNull(value) || (isString(value) && isEmptyString(value));
  const onInputChange = getOnInputChange({ onChange, type, isEmpty });
  // if setSearch is defined opt for setSearch instead of internal filtering
  const disableInternalFiltering = isDefined(setSearch);
  const displayValue = selectedValue?.title ?? value;

  const triggerContent = isEmpty ? placeholder : displayValue;
  const TriggerIcon = selectedValue?.icon;
  const defaultTooltipContent = isNull(value) || `${value}`.length < 65 ? undefined : value;

  if (!isEditing) {
    return (
      <ConditionTriggerButton
        {...props}
        isEmpty={isEmpty}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        tooltipContent={triggerTooltipContent ?? defaultTooltipContent}
        onClick={() => {
          setIsEditing(true);
        }}
      >
        {TriggerIcon}
        <EllipsizedContent>{triggerContent}</EllipsizedContent>
      </ConditionTriggerButton>
    );
  }

  return isUndefined(options) ? (
    <ConditionInputWithoutOptions
      {...props}
      autoFocus
      triggerTooltipContent={triggerTooltipContent ?? defaultTooltipContent}
      type={type}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      value={value}
      placeholder={t('storybook.placeholders.input')}
      onChange={(newValue) => !(!newValue && isEmpty) && onChange(newValue)}
      setIsEditing={setIsEditing}
      width={width ?? 'max-content'}
    />
  ) : (
    <StyledInputSelect
      {...props}
      autoFocus
      data-test={DATA_TEST.inputWithOptions}
      defaultValues={defaultValues}
      disableInternalFiltering={disableInternalFiltering}
      isVirtualized={isVirtualized}
      options={baseOptions}
      Option={isVirtualized ? TruncatedTooltipBasicOption : undefined}
      onSelect={([option]) => {
        onInputChange(option);
        setIsEditing(false);
      }}
      placeholder={t('storybook.placeholders.select')}
      onSearchChange={(filterText) => {
        disableInternalFiltering && setSearch(filterText || '');
      }}
      SelectWrapper={StyledSelectWrapper}
      SelectOptionsContainer={
        isVirtualized ? ExpandedSelectOptionsContainer : StyledSelectOptionsContainer
      }
      width={width}
    />
  );
};
